import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchTask, fetchTaskComments, fetchSubtasks, updateTask, addTaskComment, createTask } from '../api/projects';
import { fetchPeople } from '../api/people';
import type { Task, TaskComment } from '../types/project';
import type { Person } from '../types/people';
import { AssigneeDisplay } from '../components/AssigneeDropdown';
import AssigneeMultiPicker from '../components/AssigneeMultiPicker';
import { formatDateDDMMYYYY } from '../utils/date';
import { TASK_STATUSES, STATUS_LABEL } from '../utils/taskStatus';

export default function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [subtaskForm, setSubtaskForm] = useState<{
    title: string;
    description: string;
    status: Task['status'];
    priority: Task['priority'];
    assignee: string | null;
    due_date: string;
  }>({
    title: '',
    description: '',
    status: 'TO_DO',
    priority: 'MEDIUM',
    assignee: null,
    due_date: '',
  });
  const [people, setPeople] = useState<Person[]>([]);

  const load = useCallback(() => {
    if (!taskId) return;
    setLoading(true);
    Promise.all([fetchTask(taskId), fetchTaskComments(taskId), fetchPeople()])
      .then(async ([t, c, p]) => {
        setTask(t);
        setComments(c);
        setPeople(p);
        if (t) {
          const st = await fetchSubtasks(t.theme_id, t.id);
          setSubtasks(st);
        } else {
          setSubtasks([]);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [taskId]);

  const handleCreateSubtask = useCallback(async () => {
    if (!task || !subtaskForm.title.trim()) return;
    setAddingSubtask(true);
    try {
      await createTask({
        theme_id: task.theme_id,
        parent_task_id: task.id,
        title: subtaskForm.title.trim(),
        description: subtaskForm.description.trim() || undefined,
        status: subtaskForm.status,
        priority: subtaskForm.priority,
        assignee: subtaskForm.assignee ?? undefined,
        due_date: subtaskForm.due_date || undefined,
      });
      setSubtaskForm({
        title: '',
        description: '',
        status: 'TO_DO',
        priority: 'MEDIUM',
        assignee: null,
        due_date: '',
      });
      const st = await fetchSubtasks(task.theme_id, task.id);
      setSubtasks(st);
      setIsSubtaskModalOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add subtask');
    } finally {
      setAddingSubtask(false);
    }
  }, [task, subtaskForm]);

  const handleSubtaskStatusChange = useCallback(
    async (subtask: Task, status: Task['status']) => {
      try {
        await updateTask(subtask.id, { ...subtask, status });
        setSubtasks((prev) => prev.map((t) => (t.id === subtask.id ? { ...t, status } : t)));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update subtask');
      }
    },
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const handleAddComment = useCallback(async () => {
    if (!taskId || !commentText.trim()) return;
    try {
      await addTaskComment(taskId, commentText.trim());
      setCommentText('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add comment');
    }
  }, [taskId, commentText, load]);

  const handleStatusChange = useCallback(
    async (status: string) => {
      if (!task) return;
      try {
        await updateTask(task.id, { ...task, status });
        setTask((prev) => (prev ? { ...prev, status: status as Task['status'] } : null));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update');
      }
    },
    [task]
  );

  if (loading || !task) {
    return <div>{loading ? 'Loading…' : 'Task not found'}</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/projects" style={{ color: 'var(--color-blue-dark)' }}>Projects</Link>
        {' / '}
        <Link to={`/projects/theme/${task.theme_id}`} style={{ color: 'var(--color-blue-dark)' }}>Theme</Link>
        {' / '}
        <span>{task.title}</span>
      </nav>
      <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 70%', minWidth: 0 }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 16px' }}>{task.title}</h1>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Status</label>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ marginLeft: '8px', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
          {task.description && (
            <div style={{ marginBottom: '24px', padding: '16px', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '14px' }}>{task.description}</pre>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px', gap: '16px' }}>
            <span>
              {task.due_date && (
                <span style={{ color: new Date(task.due_date) < new Date() ? 'var(--color-coral)' : undefined }}>
                  Due: {formatDateDDMMYYYY(task.due_date)}
                </span>
              )}
              {!task.due_date && <span style={{ color: 'var(--color-text-muted)' }}>No due date</span>}
            </span>
            {people.length > 0 ? <AssigneeDisplay people={people} assigneeId={task.assignee} showAvatar /> : task.assignee && <span>{task.assignee}</span>}
          </div>
        </div>
        <div style={{ width: '320px', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)' }}>
              Subtasks
            </h3>
            <button
              type="button"
              onClick={() => setIsSubtaskModalOpen(true)}
              style={{
                width: 28,
                height: 28,
                borderRadius: '999px',
                border: 'none',
                background: 'var(--color-blue-dark)',
                color: 'white',
                fontSize: '18px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="Add subtask"
            >
              +
            </button>
          </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {subtasks.length === 0 && (
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>No subtasks yet.</div>
              )}
              {subtasks.map((st) => (
                <div
                  key={st.id}
                  style={{
                    padding: '10px 12px',
                    background: 'white',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <Link
                    to={`/projects/task/${st.id}`}
                    style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-blue-dark)', textDecoration: 'none' }}
                  >
                    {st.title}
                  </Link>
                  <select
                    value={st.status}
                    onChange={(e) => handleSubtaskStatusChange(st, e.target.value as Task['status'])}
                    style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', alignSelf: 'flex-start' }}
                  >
                    {TASK_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      <div style={{ marginTop: '32px', maxWidth: 720 }}>
        <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)', marginBottom: '12px' }}>
          Comments
        </h3>
        <div style={{ marginBottom: '12px' }}>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', resize: 'vertical' }}
          />
          <button
            type="button"
            onClick={handleAddComment}
            disabled={!commentText.trim()}
            style={{ marginTop: '8px', padding: '8px 16px', background: 'var(--color-blue-dark)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 500 }}
          >
            Add comment
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {comments.map((c) => (
            <div
              key={c.id}
              style={{
                padding: '12px',
                background: 'white',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                fontSize: '14px',
              }}
            >
              <p style={{ margin: '0 0 4px' }}>{c.content}</p>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{new Date(c.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      {isSubtaskModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 40,
          }}
          onClick={() => !addingSubtask && setIsSubtaskModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>New subtask</h2>
              <button
                type="button"
                onClick={() => !addingSubtask && setIsSubtaskModalOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '20px',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Title</label>
                <input
                  type="text"
                  value={subtaskForm.title}
                  onChange={(e) => setSubtaskForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Short summary"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Description</label>
                <textarea
                  value={subtaskForm.description}
                  onChange={(e) => setSubtaskForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="Details of the subtask"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Status</label>
                  <select
                    value={subtaskForm.status}
                    onChange={(e) => setSubtaskForm((f) => ({ ...f, status: e.target.value as Task['status'] }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                  >
                    {TASK_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Priority</label>
                  <select
                    value={subtaskForm.priority}
                    onChange={(e) => setSubtaskForm((f) => ({ ...f, priority: e.target.value as Task['priority'] }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <AssigneeMultiPicker
                    people={people}
                    value={subtaskForm.assignee ? [subtaskForm.assignee] : []}
                    onChange={(ids) =>
                      setSubtaskForm((f) => ({
                        ...f,
                        assignee: ids[0] ?? null,
                      }))
                    }
                    label="Assignees"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Due date</label>
                  <input
                    type="date"
                    value={subtaskForm.due_date}
                    onChange={(e) => setSubtaskForm((f) => ({ ...f, due_date: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => !addingSubtask && setIsSubtaskModalOpen(false)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '999px',
                  border: '1px solid var(--color-border)',
                  background: 'white',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSubtask}
                disabled={addingSubtask || !subtaskForm.title.trim()}
                style={{
                  padding: '8px 16px',
                  borderRadius: '999px',
                  border: 'none',
                  background: 'var(--color-blue-dark)',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: addingSubtask || !subtaskForm.title.trim() ? 'default' : 'pointer',
                  opacity: addingSubtask || !subtaskForm.title.trim() ? 0.7 : 1,
                }}
              >
                {addingSubtask ? 'Creating…' : 'Create subtask'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
}
